import {
    isTransactionColumn,
    TransactionColumn,
} from "@common/types/views/transactionViews";
import { ScheduleDrawer } from "@components/accounting/schedules/ScheduleDrawer";
import { useScheduleLifecycleActions } from "@components/accounting/schedules/applications/useScheduleLifecycleActions";
import {
    SchedulePickerTemplate,
    TransactionsTableMeta,
} from "@hooks/view/useViewTableMeta";
import { WORKSPACE_USER_CACHE_KEYS } from "@hooks/useWorkspaceUserCache";
import { isEqual } from "@common/helpers/comparison";
import { omitBy } from "@common/helpers/objects";
import { hasSpecificDateAnchor } from "@common/helpers/domains/accounting/scheduleAnchors";
import React, {
    useCallback,
    useContext,
    useEffect,
    useEffectEvent,
    useMemo,
    useRef,
    useState,
} from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useOverlays } from "contexts/OverlayContext";
import { ExcludeEmptyMockEntitiesMode } from "@hooks/useEntitiesContexts";
import { useBillingStatus } from "@hooks/useBillingStatus";
import { useCanUseInvoicing } from "@hooks/useCanUseInvoicing";
import { useSubNavigationPortalContent } from "@hooks/useSubNavigationPortal";
import { useTransactionViewConfig } from "@components/transactions/views/useTransactionViewConfig";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import { useProductTipBanner } from "@hooks/useProductTipBanner";
import { useIsAccrualLedgerEnabled } from "@hooks/useIsAccrualLedgerEnabled";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useClasses } from "@api/class.api";
import { ViewToolbar } from "@components/views/ViewToolbar/ViewToolbar";
import { TablePageHeader } from "../pageHeader/TablePageHeader";
import { FilterTabs, isActive, resetPayload } from "../pageHeader/filters/lib";
import {
    PaginatedTransactionsActionsContext,
    PaginatedTransactionsDataContext,
} from "./PaginatedTransactionsTable.context";
import { TransactionDetailsContainer } from "./TransactionDetails/TransactionDetailsContainer";
import { invalidateTransactionDetailsQueries } from "./TransactionDetails/useTransactionDetailsQuery";
import {
    TransactionsBulkActionsContextProvider,
    TransactionsBulkActionsContextProviderProps,
} from "./TransactionsBulkActions/TransactionsBulkActionsContextProvider";
import { BulkUpdateMode } from "./TransactionsBulkActions/lib";
import { useBulkActions } from "./TransactionsBulkActions/useBulkActions";
import { TransactionsExport } from "./TransactionsExport";
import "./TransactionsPage.scss";
import { TransactionsPageEmptyState } from "./TransactionsPageEmptyState";
import { DEFAULT_FILTERS, FilterProps, FiltersChangeFn } from "./filters/lib";
import { invalidateTransactionsQueries } from "./useTransactionsQuery";
import { useViewTableOverlayCta } from "./useViewTableOverlayCta";
import { useBuiltInTransactionViews } from "./views/BuiltInTransactionViews";
import { useTransactionsPageDefaultView } from "./views/useTransactionsPageDefaultView";
import { TransactionsView } from "./views/TransactionsView";
import { RulesSuggestionToggle } from "./rulesSuggestion/RulesSuggestionToggle";
import { TransactionEnrichmentPausedBanner } from "@components/accountingMigration/TransactionEnrichmentPausedBanner";
import { TransactionsProductTipBanner } from "./TransactionsProductTipBanner";
import {
    injectScheduleColumn,
    SCHEDULE_TEMPLATE_SEARCH_PARAM,
    stripInjectedScheduleColumn,
} from "./views/scheduleColumnHandoff";
import { useCanAddCashItems } from "@components/checks/AddTransactionTypePicker";

const PRODUCT_TIP_BANNER_INITIAL_HEIGHT_PX = 48;

interface ScheduleDrawerSession {
    transactionId: number;
    entityId: number;
    isOpen: boolean;
    didCreateTemplate?: boolean;
}

export const TransactionsPage: React.FC = () => {
    const { invalidate: invalidateCreditsUsed } = useBillingStatus();
    const { transactionCount, filters: queryFiltersToPass } = useContext(
        PaginatedTransactionsDataContext,
    );
    const { updateMany, setPage } = useContext(
        PaginatedTransactionsActionsContext,
    );

    const onBulkUpdated: TransactionsBulkActionsContextProviderProps["onUpdated"] =
        useCallback(
            (updatedTransactions, { mode }) => {
                if (mode === BulkUpdateMode.SELECTED) {
                    updateMany(updatedTransactions);
                } else {
                    void invalidateTransactionsQueries();
                    setPage(1);
                }

                void invalidateCreditsUsed();
                void invalidateTransactionDetailsQueries();
            },
            [invalidateCreditsUsed, setPage, updateMany],
        );

    return (
        <TransactionsBulkActionsContextProvider
            currentFilters={queryFiltersToPass}
            onUpdated={onBulkUpdated}
            totalTransactions={transactionCount}
        >
            <TransactionsPageContent />
        </TransactionsBulkActionsContextProvider>
    );
};

const TransactionsPageContent: React.FC = () => {
    const {
        shownTransactionId,
        currentPage,
        totalPages,
        currentSort,
        filters: currentFilters,
        currentView,
        initialBaseViewId,
        transactions,
    } = useContext(PaginatedTransactionsDataContext);
    const {
        setShownTransactionId,
        setPage,
        setCurrentFilters,
        setCurrentView,
    } = useContext(PaginatedTransactionsActionsContext);

    const { showProductTipBanner, dismissProductTipBanner } =
        useProductTipBanner("initialProductTipTransactions");

    // When sorting or page changes, set shown transaction to undefined
    // The case of filters changes is handled in handleFiltersChange
    const clearShownTransaction = useEffectEvent(() => {
        setShownTransactionId(undefined);
    });
    useEffect(() => {
        clearShownTransaction();
    }, [currentPage, currentSort]);

    const location = useLocation();
    const { openOverlay } = useOverlays();
    const openSplitHandledRef = useRef(false);
    const [searchParams] = useSearchParams();
    const deepLinkScheduleTemplateId =
        searchParams.get(SCHEDULE_TEMPLATE_SEARCH_PARAM) ?? undefined;
    const isScheduleColumnEnsured = deepLinkScheduleTemplateId != null;
    const [scheduleDrawerSession, setScheduleDrawerSession] =
        useState<ScheduleDrawerSession | null>(null);
    const [createdScheduleTemplate, setCreatedScheduleTemplate] =
        useState<SchedulePickerTemplate>();
    const openCellActionRef =
        useRef<TransactionsTableMeta["openCellAction"]>(undefined);

    useEffect(() => {
        if (
            !location.state?.openSplit ||
            !transactions?.length ||
            openSplitHandledRef.current
        ) {
            return;
        }
        openSplitHandledRef.current = true;
        window.history.replaceState(
            { ...window.history.state, openSplit: undefined },
            "",
        );
        openOverlay<"transactionSplit">("transactionSplit", {
            transaction: transactions[0],
        });
    }, [location.state?.openSplit, transactions, openOverlay]);

    const bulkActions = useBulkActions();
    const { isActivated: classesEnabled } = useClasses();
    const builtInViews = useBuiltInTransactionViews();
    const defaultView = useTransactionsPageDefaultView();

    const isAccrualLedgerFilterEnabled = useIsAccrualLedgerEnabled();
    const { enableSimilarityRules } = useTypedFlags();

    const transactionsViewConfig = useTransactionViewConfig();

    const viewToolbarContent = useMemo(
        () => (
            <ViewToolbar
                view={currentView}
                onViewChange={setCurrentView}
                viewConfig={transactionsViewConfig}
                builtInViews={builtInViews}
                defaultView={defaultView}
                defaultFilters={DEFAULT_FILTERS}
                baseViewCacheKey={
                    WORKSPACE_USER_CACHE_KEYS.TRANSACTIONS_PAGE_BASE_VIEW_ID_CACHE_KEY
                }
                initialBaseViewId={initialBaseViewId}
            />
        ),
        [
            currentView,
            setCurrentView,
            builtInViews,
            defaultView,
            transactionsViewConfig,
            initialBaseViewId,
        ],
    );

    useSubNavigationPortalContent(
        "transactions-view-toolbar",
        viewToolbarContent,
    );

    // Refs to keep handleFiltersChange stable. Without these, every filter or
    // bulk-action change recreates the callback → FiltersOverlay re-renders.
    const currentFiltersRef = useRef(currentFilters);
    currentFiltersRef.current = currentFilters;

    const bulkActionsRef = useRef(bulkActions);
    bulkActionsRef.current = bulkActions;

    const handleFiltersChange = useCallback<FiltersChangeFn>(
        (value) => {
            const filters = currentFiltersRef.current;
            const newFilters = omitBy(
                { ...filters, ...value },
                (v) => v === null || v === undefined,
            );

            if (isEqual(newFilters, filters)) {
                return;
            }

            bulkActionsRef.current.clearSelection();
            setPage(1);
            setShownTransactionId(undefined);
            setCurrentFilters(newFilters);
        },
        [setPage, setShownTransactionId, setCurrentFilters],
    );

    const canAddCashItems = useCanAddCashItems();
    const isInvoicingEnabled = useCanUseInvoicing();
    useEffect(() => {
        const hasInvoicingFilter =
            isActive.customers(currentFilters) ||
            isActive.billableExpenseInvoiceStatuses(currentFilters);
        if (isInvoicingEnabled || !hasInvoicingFilter) {
            return;
        }
        handleFiltersChange({
            ...resetPayload.customers,
            ...resetPayload.billableExpenseInvoiceStatuses,
        });
    }, [isInvoicingEnabled, currentFilters, handleFiltersChange]);

    const availableFilters = useMemo(
        () => [
            FilterTabs.DATE,
            FilterTabs.CATEGORY,
            FilterTabs.ACCOUNT,
            ...(classesEnabled ? [FilterTabs.CLASS] : []),
            FilterTabs.AMOUNT,
            FilterTabs.RULES,
            ...(enableSimilarityRules ? [FilterTabs.SIMILAR_TO] : []),
            FilterTabs.COUNTERPARTY,
            ...(isInvoicingEnabled
                ? [
                      FilterTabs.CUSTOMER,
                      FilterTabs.BILLABLE_EXPENSE_INVOICE_STATUSES,
                  ]
                : []),
            FilterTabs.NOTE,
            FilterTabs.RECEIPTS,
            FilterTabs.SPLIT,
            FilterTabs.RECONCILIATION,
            ...(canAddCashItems ? [FilterTabs.CASH_ITEM_TYPE] : []),
            FilterTabs.MATCHED,
            FilterTabs.LEDGER,
            ...(isAccrualLedgerFilterEnabled
                ? [FilterTabs.ACCRUAL_LEDGER]
                : []),
            FilterTabs.LEDGER_GROUP,
            FilterTabs.TRANSACTION_LOCKED,
            FilterTabs.STATUS,
            FilterTabs.REVIEW,
            FilterTabs.EXCLUDE_1099,
        ],
        [
            classesEnabled,
            isInvoicingEnabled,
            enableSimilarityRules,
            isAccrualLedgerFilterEnabled,
            canAddCashItems,
        ],
    );

    const renderEmptyState = useCallback(
        () => <TransactionsPageEmptyState />,
        [],
    );

    const [prependContent, appendContent] = useViewTableOverlayCta({
        filters: currentFilters,
        currentSort,
        currentPage,
        totalPages,
    });

    const filterProps: FilterProps = {
        filters: currentFilters,
        onChange: handleFiltersChange,
    };

    const [detailInitialOpenPanels, setDetailInitialOpenPanels] = useState<
        string[] | undefined
    >(undefined);

    const setSelectedTransactionId = useCallback(
        (id: number | string | undefined) => {
            setShownTransactionId(id as number);
            setDetailInitialOpenPanels(undefined);
        },
        [setShownTransactionId],
    );

    const handleOpenTransactionReview = useCallback(
        (id: number) => {
            setShownTransactionId(id);
            setDetailInitialOpenPanels(["review"]);
        },
        [setShownTransactionId],
    );

    const [productTipCtaHeightPx, setProductTipCtaHeightPx] = useState(
        PRODUCT_TIP_BANNER_INITIAL_HEIGHT_PX,
    );

    const visibleTransactionView = useMemo(
        () =>
            isScheduleColumnEnsured
                ? injectScheduleColumn(currentView)
                : currentView,
        [currentView, isScheduleColumnEnsured],
    );
    const openCreateSchedule = useCallback(
        (options: { transactionId: number; entityId: number }) => {
            setScheduleDrawerSession({
                transactionId: options.transactionId,
                entityId: options.entityId,
                isOpen: true,
            });
        },
        [],
    );
    const closeScheduleDrawer = useCallback(() => {
        setScheduleDrawerSession(
            (session) => session && { ...session, isOpen: false },
        );
    }, []);
    const finishScheduleDrawerClose = useCallback(() => {
        if (scheduleDrawerSession?.didCreateTemplate) {
            openCellActionRef.current?.({
                columnId: TransactionColumn.SCHEDULE,
                rowId: scheduleDrawerSession.transactionId,
            });
        }
        setScheduleDrawerSession(null);
    }, [scheduleDrawerSession]);
    const prioritizedScheduleTemplateId =
        createdScheduleTemplate?.id ?? deepLinkScheduleTemplateId;
    const { requestLifecycleAction, modals: scheduleLifecycleModals } =
        useScheduleLifecycleActions({
            onCompleted: () => void invalidateTransactionDetailsQueries(),
        });
    const scheduleTableMeta = useMemo<TransactionsTableMeta>(
        () => ({
            prioritizedScheduleTemplateId,
            prioritizedScheduleTemplate: createdScheduleTemplate,
            callbacks: {
                onCreateSchedule: openCreateSchedule,
                onRequestLifecycleAction: requestLifecycleAction,
            },
        }),
        [
            createdScheduleTemplate,
            openCreateSchedule,
            prioritizedScheduleTemplateId,
            requestLifecycleAction,
        ],
    );

    const recomputeTopOffsetOn = useMemo(
        () => [showProductTipBanner, productTipCtaHeightPx],
        [showProductTipBanner, productTipCtaHeightPx],
    );

    return (
        <section className="transactions">
            <TransactionEnrichmentPausedBanner />
            {showProductTipBanner ? (
                <TransactionsProductTipBanner
                    onHeightChange={setProductTipCtaHeightPx}
                    onDismiss={dismissProductTipBanner}
                />
            ) : null}
            <TablePageHeader
                search={filterProps.filters.search}
                onSearchChange={(value) => {
                    filterProps.onChange({ search: value ?? "" });
                }}
                searchKeyboardShortcuts
                filters={filterProps.filters}
                onFiltersChange={filterProps.onChange}
                defaultFilters={DEFAULT_FILTERS}
                hideClearButton
                availableFilters={availableFilters}
                ledgerFilterLabel="Cash ledger"
                isFullPage
            >
                <EntityFilterCombobox
                    filters={filterProps.filters}
                    onChange={filterProps.onChange}
                    excludeEmptyMockEntities={
                        ExcludeEmptyMockEntitiesMode.REQUIRE_TRANSACTIONS
                    }
                />
                <RulesSuggestionToggle />
                <TransactionsExport
                    currentFilters={currentView.filters}
                    currentColumns={currentView.columns
                        .map((column) => column.id)
                        .filter(isTransactionColumn)}
                    currentSort={currentSort}
                />
            </TablePageHeader>
            <section className="transactions__body">
                <TransactionsView
                    view={visibleTransactionView}
                    setSelectedTransactionId={setSelectedTransactionId}
                    onOpenTransactionReview={handleOpenTransactionReview}
                    selectedTransactionId={shownTransactionId}
                    emptyView={renderEmptyState}
                    prependContent={prependContent}
                    appendContent={appendContent}
                    fixedTopOffsetPx
                    recomputeTopOffsetOn={recomputeTopOffsetOn}
                    customTableMeta={scheduleTableMeta}
                    openCellActionRef={openCellActionRef}
                    transformViewChange={
                        isScheduleColumnEnsured
                            ? stripInjectedScheduleColumn
                            : undefined
                    }
                />

                {shownTransactionId ? (
                    <TransactionDetailsContainer
                        id={shownTransactionId}
                        onClose={() => setShownTransactionId()}
                        recomputeTopOffsetOn={recomputeTopOffsetOn}
                        initialOpenPanels={detailInitialOpenPanels}
                        scheduleCallbacks={scheduleTableMeta.callbacks}
                    />
                ) : null}
                {scheduleLifecycleModals}
                <ScheduleDrawer
                    open={scheduleDrawerSession?.isOpen ?? false}
                    templateId={null}
                    initialEntityId={scheduleDrawerSession?.entityId}
                    onClose={closeScheduleDrawer}
                    onAfterClose={finishScheduleDrawerClose}
                    onCreated={(template) => {
                        setCreatedScheduleTemplate({
                            id: template.id,
                            name: template.name,
                            group: template.group,
                            entityIds: Array.from(
                                new Set(
                                    template.accountRows.map(
                                        (accountRow) => accountRow.entityId,
                                    ),
                                ),
                            ),
                            hasSpecificDateAnchor: hasSpecificDateAnchor(
                                template.components,
                            ),
                            type: template.type,
                            revenueSubMode: template.revenueSubMode,
                            expenseSubMode: template.expenseSubMode,
                        });
                        setScheduleDrawerSession(
                            (session) =>
                                session && {
                                    ...session,
                                    didCreateTemplate: true,
                                },
                        );
                    }}
                />
            </section>
        </section>
    );
};
